export const METRIC_CATEGORIES = {
  Activity: [
    "steps",
    "calories",
    "distance",
    "distanceCycling",
    "distanceSwimming",
    "distanceWheelchair",
    "flightsClimbed",
    "activeMinutesLight",
    "activeMinutesModerate",
    "activeMinutesVigorous",
    "exerciseTime",
    "standTime",
    "swimmingStrokeCount",
    "basalEnergyBurned",
  ],
  Heart: [
    "restingHeartRate",
    "walkingHeartRateAvg",
    "heartRateZones",
    "heartRateRecovery",
  ],
  Sleep: [
    "sleepDuration",
    "sleepEfficiency",
    "sleepStages",
    "timeInBed",
    "bedtime",
    "waketime",
  ],
  Body: [
    "weight",
    "height",
    "bodyFat",
    "bmi",
    "leanBodyMass",
    "waistCircumference",
  ],
  Vitals: [
    "breathingRate",
    "hrvRmssd",
    "hrvRmssdCv",
    "hrvSdnn",
    "vo2Max",
    "oxygenSaturationAvg",
    "oxygenSaturationMin",
    "oxygenSaturationMax",
    "bloodPressureSystolic",
    "bloodPressureDiastolic",
    "bodyTemperature",
    "basalBodyTemperature",
    "skinTemperature",
    "coreTemperature",
  ],
  Nutrition: [
    "dietaryEnergy",
    "dietaryProtein",
    "dietaryCarbohydrates",
    "dietaryFatTotal",
    "dietarySugar",
    "dietaryFiber",
    "dietaryWater",
    "dietaryCaffeine",
    "foodLog",
    "waterIntake",
  ],
  Mobility: [
    "walkingSpeedAvg",
    "walkingStepLength",
    "walkingAsymmetryPercentage",
    "walkingDoubleSupportPercentage",
    "stairAscentSpeed",
    "stairDescentSpeed",
    "sixMinuteWalkDistance",
    "mindfulMinutesTotal",
    "mindfulSessions",
  ],
  Hearing: ["environmentalAudioExposureAvg", "headphoneAudioExposureAvg"],
  Other: ["azmFatBurn", "azmCardio", "azmPeak", "uvExposure", "timeInDaylight"],
} as const;

export const METRIC_LABELS: Record<string, string> = {
  steps: "Steps",
  calories: "Calories",
  distance: "Distance (Walking/Running)",
  distanceCycling: "Distance (Cycling)",
  distanceSwimming: "Distance (Swimming)",
  distanceWheelchair: "Distance (Wheelchair)",
  flightsClimbed: "Flights Climbed",
  activeMinutesLight: "Active Minutes (Light)",
  activeMinutesModerate: "Active Minutes (Moderate)",
  activeMinutesVigorous: "Active Minutes (Vigorous)",
  exerciseTime: "Exercise Time",
  standTime: "Stand Time",
  swimmingStrokeCount: "Swimming Stroke Count",
  restingHeartRate: "Resting Heart Rate",
  walkingHeartRateAvg: "Walking Heart Rate (Avg)",
  heartRateZones: "Heart Rate Zones",
  sleepDuration: "Sleep Duration",
  sleepEfficiency: "Sleep Efficiency",
  sleepStages: "Sleep Stages",
  timeInBed: "Time in Bed",
  bedtime: "Bedtime",
  waketime: "Waketime",
  weight: "Weight",
  height: "Height",
  bodyFat: "Body Fat",
  bmi: "BMI",
  leanBodyMass: "Lean Body Mass",
  waistCircumference: "Waist Circumference",
  breathingRate: "Breathing Rate",
  hrvRmssd: "HRV (RMSSD)",
  hrvRmssdCv: "HRV (RMSSD CV)",
  hrvSdnn: "HRV (SDNN)",
  heartRateRecovery: "Heart Rate Recovery",
  vo2Max: "VO2 Max",
  oxygenSaturationAvg: "Oxygen Saturation (Avg)",
  oxygenSaturationMin: "Oxygen Saturation (Min)",
  oxygenSaturationMax: "Oxygen Saturation (Max)",
  azmFatBurn: "Active Zone Minutes (Fat Burn)",
  azmCardio: "Active Zone Minutes (Cardio)",
  azmPeak: "Active Zone Minutes (Peak)",
  skinTemperature: "Skin Temperature",
  coreTemperature: "Core Temperature",
  bodyTemperature: "Body Temperature",
  basalBodyTemperature: "Basal Body Temperature",
  bloodPressureSystolic: "Blood Pressure (Systolic)",
  bloodPressureDiastolic: "Blood Pressure (Diastolic)",
  waterIntake: "Water Intake",
  dietaryEnergy: "Dietary Energy Consumed",
  dietaryProtein: "Dietary Protein",
  dietaryCarbohydrates: "Dietary Carbohydrates",
  dietaryFatTotal: "Dietary Fat Total",
  dietarySugar: "Dietary Sugar",
  dietaryFiber: "Dietary Fiber",
  dietaryWater: "Dietary Water",
  dietaryCaffeine: "Dietary Caffeine",
  foodLog: "Food Log",
  mindfulMinutesTotal: "Mindful Minutes",
  mindfulSessions: "Mindful Sessions",
  walkingSpeedAvg: "Walking Speed (Avg)",
  walkingStepLength: "Walking Step Length",
  walkingAsymmetryPercentage: "Walking Asymmetry (%)",
  walkingDoubleSupportPercentage: "Walking Double Support (%)",
  stairAscentSpeed: "Stair Ascent Speed",
  stairDescentSpeed: "Stair Descent Speed",
  sixMinuteWalkDistance: "Six Minute Walk Distance",
  environmentalAudioExposureAvg: "Environmental Audio Exposure (Avg)",
  headphoneAudioExposureAvg: "Headphone Audio Exposure (Avg)",
  uvExposure: "UV Exposure",
  timeInDaylight: "Time in Daylight",
  basalEnergyBurned: "Basal Energy Burned",
};

export const METRIC_UNITS: Record<string, string> = {
  steps: "steps",
  calories: "kcal",
  distance: "km",
  distanceCycling: "km",
  distanceSwimming: "km",
  distanceWheelchair: "km",
  flightsClimbed: "flights",
  activeMinutesLight: "min",
  activeMinutesModerate: "min",
  activeMinutesVigorous: "min",
  exerciseTime: "min",
  standTime: "min",
  swimmingStrokeCount: "strokes",
  restingHeartRate: "bpm",
  walkingHeartRateAvg: "bpm",
  heartRateZones: "(JSON)",
  sleepDuration: "min",
  sleepEfficiency: "%",
  sleepStages: "(JSON)",
  timeInBed: "min",
  bedtime: "(timestamp)",
  waketime: "(timestamp)",
  weight: "kg",
  height: "cm",
  bodyFat: "%",
  bmi: "kg/m²",
  leanBodyMass: "kg",
  waistCircumference: "cm",
  breathingRate: "breaths/min",
  hrvRmssd: "ms",
  hrvRmssdCv: "%",
  hrvSdnn: "ms",
  heartRateRecovery: "bpm",
  vo2Max: "mL/(kg·min)",
  oxygenSaturationAvg: "%",
  oxygenSaturationMin: "%",
  oxygenSaturationMax: "%",
  azmFatBurn: "min",
  azmCardio: "min",
  azmPeak: "min",
  skinTemperature: "°C",
  coreTemperature: "°C",
  bodyTemperature: "°C",
  basalBodyTemperature: "°C",
  bloodPressureSystolic: "mmHg",
  bloodPressureDiastolic: "mmHg",
  waterIntake: "L",
  dietaryEnergy: "kcal",
  dietaryProtein: "g",
  dietaryCarbohydrates: "g",
  dietaryFatTotal: "g",
  dietarySugar: "g",
  dietaryFiber: "g",
  dietaryWater: "mL",
  dietaryCaffeine: "mg",
  foodLog: "(JSON)",
  mindfulMinutesTotal: "min",
  mindfulSessions: "sessions",
  walkingSpeedAvg: "m/s",
  walkingStepLength: "cm",
  walkingAsymmetryPercentage: "%",
  walkingDoubleSupportPercentage: "%",
  stairAscentSpeed: "m/s",
  stairDescentSpeed: "m/s",
  sixMinuteWalkDistance: "meters",
  environmentalAudioExposureAvg: "dB",
  headphoneAudioExposureAvg: "dB",
  uvExposure: "UV index",
  timeInDaylight: "min",
  basalEnergyBurned: "kcal",
};

const FITBIT_METRICS = new Set<string>([
  "steps",
  "calories",
  "distance",
  "activeMinutesLight",
  "activeMinutesModerate",
  "activeMinutesVigorous",
  "restingHeartRate",
  "heartRateZones",
  "sleepDuration",
  "sleepEfficiency",
  "sleepStages",
  "bedtime",
  "waketime",
  "weight",
  "bodyFat",
  "bmi",
  "breathingRate",
  "hrvRmssd",
  "hrvRmssdCv",
  "vo2Max",
  "oxygenSaturationAvg",
  "oxygenSaturationMin",
  "oxygenSaturationMax",
  "azmFatBurn",
  "azmCardio",
  "azmPeak",
  "skinTemperature",
  "coreTemperature",
  "waterIntake",
  "foodLog",
]);

const HEALTHKIT_METRICS = new Set<string>([
  "steps",
  "calories",
  "distance",
  "distanceCycling",
  "distanceSwimming",
  "distanceWheelchair",
  "flightsClimbed",
  "exerciseTime",
  "standTime",
  "swimmingStrokeCount",
  "restingHeartRate",
  "walkingHeartRateAvg",
  "sleepDuration",
  "sleepStages",
  "timeInBed",
  "weight",
  "height",
  "bodyFat",
  "bmi",
  "leanBodyMass",
  "waistCircumference",
  "breathingRate",
  "hrvSdnn",
  "heartRateRecovery",
  "vo2Max",
  "oxygenSaturationAvg",
  "bodyTemperature",
  "basalBodyTemperature",
  "bloodPressureSystolic",
  "bloodPressureDiastolic",
  "dietaryEnergy",
  "dietaryProtein",
  "dietaryCarbohydrates",
  "dietaryFatTotal",
  "dietarySugar",
  "dietaryFiber",
  "dietaryWater",
  "dietaryCaffeine",
  "foodLog",
  "mindfulMinutesTotal",
  "mindfulSessions",
  "walkingSpeedAvg",
  "walkingStepLength",
  "walkingAsymmetryPercentage",
  "walkingDoubleSupportPercentage",
  "stairAscentSpeed",
  "stairDescentSpeed",
  "sixMinuteWalkDistance",
  "environmentalAudioExposureAvg",
  "headphoneAudioExposureAvg",
  "uvExposure",
  "timeInDaylight",
  "basalEnergyBurned",
]);

export const getMetricsForDeviceType = (
  deviceType: "fitbit" | "healthkit" | "both" | "none",
): string[] => {
  if (deviceType === "none") return [];
  if (deviceType === "fitbit") return Array.from(FITBIT_METRICS);
  if (deviceType === "healthkit") return Array.from(HEALTHKIT_METRICS);

  return Array.from(
    new Set<string>([
      ...Array.from(FITBIT_METRICS),
      ...Array.from(HEALTHKIT_METRICS),
    ]),
  );
};
